/**
 * Feature-flag admin API client (trunk-based CI/CD + feature flags).
 * Mirrors backend/src/routes/flags.ts.
 */
import type {
  ListFlagsResponse,
  FlagMutationResponse,
  FlagStage,
  FlagView,
} from '../../../../shared/types/flags';
import { authFetch } from './authFetch';
import { API_BASE } from './version';

async function readErr(r: Response, fallback: string): Promise<string> {
  try {
    const j = (await r.json()) as { error?: { message?: string } };
    if (j?.error?.message) return j.error.message;
  } catch {
    /* */
  }
  return fallback;
}

/** GET /api/v1/flags — registry + live global state (stages + kills). */
export async function fetchFlags(): Promise<ListFlagsResponse> {
  const r = await authFetch(`${API_BASE}/flags`);
  if (!r.ok) throw new Error(await readErr(r, 'Failed to load feature flags'));
  return (await r.json()) as ListFlagsResponse;
}

/** PUT /api/v1/flags/:name/stage — set a flag's GLOBAL stage. */
export async function setFlagStage(name: string, stage: FlagStage): Promise<FlagView> {
  const r = await authFetch(`${API_BASE}/flags/${encodeURIComponent(name)}/stage`, {
    method: 'PUT',
    body: JSON.stringify({ stage }),
  });
  if (!r.ok) throw new Error(await readErr(r, 'Failed to update flag stage'));
  return ((await r.json()) as FlagMutationResponse).flag;
}

/** PUT /api/v1/flags/:name/kill — kill (true) or revive (false) a flag. */
export async function setFlagKill(name: string, killed: boolean): Promise<FlagView> {
  const r = await authFetch(`${API_BASE}/flags/${encodeURIComponent(name)}/kill`, {
    method: 'PUT',
    body: JSON.stringify({ killed }),
  });
  if (!r.ok) throw new Error(await readErr(r, 'Failed to update flag kill state'));
  return ((await r.json()) as FlagMutationResponse).flag;
}
